import { defineStore } from 'pinia';
import { computed, watch, ref } from 'vue';
import { computedAsync } from '@vueuse/core';

import {
  ActivityIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type {
  FlightIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isActivityIngredient,
  isFerryIngredient,
  isFlightIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  CustomerOriginQueryParamValue,
  TripPlannerPage,
} from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useGeolocationStore,
  useRouterStore,
  useStatsigEventsStore,
  useUserLocationStore,
  usePriceCalendarStore,
  useQueryParamsStore,
  useTripStore,
  useEditingIngredientStore,
  useItineraryStore,
  useGlobalCurrencyStore,
} from '@apps/main/new-desktop/core/stores';
import type {
  AddFlightsModalInitialRoute,
  AddFlightsModalRouteStopData,
  AddFligthsModalRoute,
  RouteData,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { OwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/ownTransportIngredient';

import { ConnectionType } from '@libs/maps';
import type {
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  HeartedHotel,
  IngredientId,
  PortInfo,
  SearchTerm,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  MyPositionType,
  isBookableTransportType,
  TransportType,
  isOwnTransportType,
} from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  isBookableTransport,
  readonlyRef,
} from '@libs/utils';
import { isExperimentAsync, isFeatureToggleActive } from '@libs/feature-toggle';
import { isStena } from '@libs/cookie';
import type { TripOverviewInitiatedMetadata } from '@libs/google-analytics-events';
import { trackTripOverviewInitiated } from '@libs/google-analytics-events';

import type {
  AlternativeRoomSuggestionAccommodationSegment,
  ItinerartBookableSegmentType,
  ItineraryBookableTransportSegment,
  ItineraryLoaderSegment,
  ItineraryLocationLoaderSegment,
  ItineraryLocationSegment,
  ItineraryNoTransportLoaderSegment,
  ItineraryNoTransportSegment,
  ItinerarySegment,
  ItinerarySimpleTransportSegment,
  ItineraryTransportLoaderSegment,
  UnavailabilityReason,
  TripOverviewSegments,
  ItineraryUpsellNoTransportSegment,
  ItineraryStenaUpsellNoTransportSegment,
  AlternativeTicketSuggestionActivitySegment,
  ItineraryMiddleLocationSegment,
} from '../models';
import {
  ItinerarySegmentType,
  TripOverviewTemplateIds,
  isItineraryBookableTransport,
  isItineraryMiddleLocationSegment,
} from '../models';

import { getTripItineraryLoaderSegments } from './tripOverviewLoaderLogic';
import { useTripOverviewModalsStore } from './tripOverviewModalsStore';
import { getTripOverviewSegments } from './tripOverviewSegments';
import { mapTransportTypeToConnectionType } from './tripOverviewSegments/tripOverviewStoreSegmentsLogic';
import {
  getAutocompleteTypeFromLocationSegment,
  getFerryDetails,
  getFlightDetails,
  getHomeFlightData,
  getSearchTextFromLocationSegment,
  updateFlightIngredientsWithAlternativeSuggestion,
} from './tripOverviewStoreLogic';
import { useUpsellStore } from './upsell/upsellStore';
import { getUnavailableAccommodationMessage } from './tripOverviewSegments/tripOverviewHotelRelatedSegmentsLogic';
import {
  getUnavailableFlightMessage,
  getUnavailableFerryMessage,
} from './tripOverviewSegments/tripOverviewBookableTransportSegmentLogic';
import { getUnavailableActivityMessage } from './tripOverviewSegments/tripOverviewActivitySegmentsLogic';

export const useTripOverviewPageStore = defineStore(
  'TripOverviewPageStore',
  () => {
    const mapStore = useMapStore();
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const itineraryStore = useItineraryStore();
    const upsellStore = useUpsellStore();
    const routerStore = useRouterStore();
    const tripOverviewModalsStore = useTripOverviewModalsStore();
    const editingIngredientStore = useEditingIngredientStore();
    const geolocationStore = useGeolocationStore();
    const statsigEventsStore = useStatsigEventsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const browserStore = useBrowserStore();
    const userLocationStore = useUserLocationStore();
    const priceCalendarStore = usePriceCalendarStore();
    const queryParamsStore = useQueryParamsStore();
    const globalCurrencyStore = useGlobalCurrencyStore();

    const isFirstTripItineraryRender =
      routerStore.landingRoute === TripPlannerPage.TripOverview;

    const hasUserSelectedTransport = ref<boolean>(false);

    // Actions
    function initializeView(): void {
      loadUserGeolocationToMapState();
      const metadata: TripOverviewInitiatedMetadata = {
        totalAccomodation: recipeStore.hotelOnlyRecipe.length,
        totalFerry: recipeStore.ferryOnlyRecipe.length,
        totalFlight: recipeStore.flightOnlyRecipe.length,
        totalActivity: recipeStore.activityOnlyRecipe.length,
      };
      statsigEventsStore.logTripOverviewInitiated(metadata);
      trackTripOverviewInitiated(metadata);
    }

    function setUserSelectedTransport(value: boolean): void {
      hasUserSelectedTransport.value = value;
    }

    function deleteIngredientById(ingredientId: IngredientId): void {
      recipeStore.deleteIngredientById(ingredientId);
    }

    function confirmAlternativeFlight(ingredientId: IngredientId): void {
      const flightIngredientResponse =
        itineraryStore.getFlightIngredientResponse(ingredientId);

      const flightResponses =
        itineraryStore.selectedItinerary?.flightOnlyItinerary;

      assertValue(
        flightResponses,
        'tripOverviewPageStore.ts > confirmAlternativeFlight > flightResponses'
      );

      const flightsInPackage = flightResponses.filter(
        (flight) =>
          flight.alternativeSuggestion?.addToBasketToken ===
          flightIngredientResponse?.alternativeSuggestion?.addToBasketToken
      );

      flightsInPackage.forEach((flight) => {
        const alternativeFlight = flight.alternativeSuggestion;

        assertValue(
          alternativeFlight,
          'tripOverviewPageStore.ts > confirmAlternativeFlight > alternativeFlight'
        );

        updateFlightIngredientsWithAlternativeSuggestion(
          flight.ingredientId,
          alternativeFlight,
          recipeStore.getFlightIngredient
        );
      });
    }

    function getFromPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.fromPort,
        label: ingredient.fromPortDisplayText,
        name: ingredient.fromPortText,
      };
    }

    function getToPortInfoFromEditingIngredient(): PortInfo | undefined {
      const ingredient = editingIngredientStore.editingTransportIngredient;
      if (!ingredient) {
        return undefined;
      }
      return {
        code: ingredient.toPort,
        label: ingredient.toPortDisplayText,
        name: ingredient.toPortText,
      };
    }

    // Getters

    const numberOfDestinations = computed<number>(
      () => recipeStore.stayOnlyRecipe.length
    );

    const numberOfNights = computed<number>(() =>
      recipeStore.stayOnlyRecipe.reduce((acc, hotel) => {
        if (hotel.numberOfNights) {
          acc += hotel.numberOfNights;
        }
        return acc;
      }, 0)
    );

    const hasRecipeIngredients = computed(
      () => recipeStore.hasRecipeIngredients
    );

    const homeFlightData = computed(() =>
      getHomeFlightData(recipeStore.recipe)
    );

    const isDateLocationSegmentExperiment = computedAsync<boolean>(
      () => isExperimentAsync('date_location_segment'),
      false
    );

    const tripItinerary = computed<ItinerarySegment[]>(() => {
      const tripOverviewSegments: TripOverviewSegments = {
        chronologicalItinerary: tripStore.chronologicalItinerary,
        startLocation: userLocationStore.startLocation,
        endLocation: userLocationStore.endLocation,
        getIngredient: recipeStore.getIngredient,
        getIngredientIndex: recipeStore.getIngredientIndex,
        paxes: recipeStore.pax.rooms,
        isSuggestedHotel: tripStore.isSuggestedHotel,
        isSuggestedTransport: tripStore.isSuggestedTransport,
        endLocationIngredient: recipeStore.endLocationIngredient,
        calculatedRoute: mapStore.calculatedRoute,
        ferryTicketCategoryDictionary:
          upsellStore.ferryTicketCategoryDictionary,
        userSelectedFerryTicketCategory:
          upsellStore.userSelectedFerryTicketCategory,
        ferryVehicleTypes: tripStore.ferryVehicleTypes,
        areThereActivitiesInDestination:
          tripStore.areThereActivitiesInDestination,
        getActivitySuggestions: itineraryStore.getActivitySuggestions,
        getStaticActivitySuggestions:
          itineraryStore.getDestinationSuggestedActivities,
        isSmallDevice: browserStore.isSmallerThan1200px,
        selectedDate: preferredTimeStore.preferredDay,
        getFlightAllCarriers: (ingredientId: IngredientId) =>
          tripStore.getFlightAllCarriers(ingredientId).allCarriers,
        getFerryAllCarriers: (ingredientId: IngredientId) =>
          tripStore.getFerryAllCarriers(ingredientId).allCarriers,
        isUserFromBookingPage:
          queryParamsStore.queryParams?.customerOrigin ===
          CustomerOriginQueryParamValue.BookingPage,
        hasUserSelectedTransport: hasUserSelectedTransport.value,
        isDateLocationSegmentExperiment: isDateLocationSegmentExperiment.value,
        isUserFromStena: isStena(),
        currency: globalCurrencyStore.globalCurrency || '',
        moneyFormanterFn: globalCurrencyStore.formatMoney,
      };
      const result = getTripOverviewSegments(tripOverviewSegments);

      return result;
    });

    const isLoading = computed(() => itineraryStore.isSearchingItineraries);
    const isLoadingSimpleTransportSegmentDetails = computed(
      () => mapStore.isLoadingCalculatedRoute || isFeatureToggleActive('loader')
    );

    const anyItineraryLoaded = computed(() => tripItinerary.value.length > 0);

    const tripItineraryLoader = computed<ItineraryLoaderSegment[]>(() =>
      getTripItineraryLoaderSegments(
        recipeStore.recipe,
        !!geolocationStore.userGeolocation,
        browserStore.isSmallerThan900px,
        (ingredientId: IngredientId) =>
          tripStore.getFlightAllCarriers(ingredientId).allCarriers,
        (ingredientId: IngredientId) =>
          tripStore.getFerryAllCarriers(ingredientId).allCarriers,
        globalCurrencyStore.globalCurrency || '',
        isDateLocationSegmentExperiment.value
      )
    );

    function selectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment,
      newTransportType: TransportType
    ): void {
      if (
        segment.segmentType === ItinerarySegmentType.Flight &&
        newTransportType !== TransportType.Flight
      ) {
        statsigEventsStore.logTripOverviewFlightRemoved();
      }

      if (
        segment.segmentType === ItinerarySegmentType.Ferry &&
        newTransportType !== TransportType.Ferry
      ) {
        statsigEventsStore.logTripOverviewFerryRemoved();
      }
      const ingredient = getIngredientForSelectTransport(segment);
      const newTransportConnectionType =
        mapTransportTypeToConnectionType(newTransportType);

      const ingredientIndex = recipeStore.getIngredientIndex(
        segment.ingredientId
      );
      const isLastTransport = ingredientIndex === recipeStore.recipe.length - 1;
      setEditingIngredient(ingredient);

      if (
        ingredient &&
        segment.segmentType.toLocaleLowerCase() ===
          newTransportType.toLocaleLowerCase()
      ) {
        if (segment.segmentType === ItinerarySegmentType.Flight) {
          routerStore.navigateToFlightsList();

          return;
        }
        if (segment.segmentType === ItinerarySegmentType.Ferry) {
          routerStore.navigateToFerryList();

          return;
        }
        if (
          isItineraryBookableTransport(segment) &&
          isTransportIngredient(ingredient)
        ) {
          editingIngredientStore.setTransportEditingIngredient(
            ingredient,
            'recipe'
          );
        }
        return;
      }

      if (isItineraryBookableTransport(segment)) {
        if (ingredient) {
          recipeStore.deleteIngredient(ingredient);
        }
        if (isLastTransport) {
          const endLocationIngredient = recipeStore.endLocationIngredient;

          setEditingIngredient(endLocationIngredient);
        } else {
          const newIngredientToSetTransport =
            recipeStore.recipe[ingredientIndex];
          setEditingIngredient(newIngredientToSetTransport);
        }
      }
      if (
        isBookableTransport(newTransportConnectionType) &&
        isBookableTransportType(newTransportType)
      ) {
        recipeStore.createTransportIngredient(newTransportConnectionType);

        if (ingredient) {
          if (isOwnTransportIngredient(ingredient)) {
            ingredient.setOwnTransportType(TransportType.None);
          }
        }
        if (newTransportConnectionType === ConnectionType.FLIGHT) {
          tripOverviewModalsStore.openAddFlightsModal();
        } else {
          tripOverviewModalsStore.openPortSelectionModal(
            editingIngredientStore.editingTransportIngredient?.ingredientId,
            getFromPortInfoFromEditingIngredient(),
            getToPortInfoFromEditingIngredient(),
            newTransportType,
            false
          );
        }
      } else {
        if (isOwnTransportType(newTransportType) && ingredient) {
          if (isOwnTransportIngredient(ingredient)) {
            ingredient.setOwnTransportType(newTransportType);
          } else {
            const newOwnTransportIngredient = new OwnTransportIngredient(
              newTransportType
            );
            recipeStore.addIngredient(
              newOwnTransportIngredient,
              ingredientIndex
            );
          }
        }
      }
    }

    function getIngredientForSelectTransport(
      segment:
        | ItineraryNoTransportSegment
        | ItineraryBookableTransportSegment
        | ItinerarySimpleTransportSegment
        | ItineraryTransportLoaderSegment
        | ItineraryNoTransportLoaderSegment
        | ItineraryUpsellNoTransportSegment
        | ItineraryStenaUpsellNoTransportSegment
    ): RecipeMainIngredient | undefined {
      let ingredient: RecipeMainIngredient | undefined;

      const recipeIngredient = recipeStore.getIngredient(segment.ingredientId);
      if (
        recipeIngredient &&
        (isTransportIngredient(recipeIngredient) ||
          isStayIngredient(recipeIngredient) ||
          isOwnTransportIngredient(recipeIngredient))
      ) {
        ingredient = recipeIngredient;
      }

      return ingredient;
    }

    function setEditingIngredient(
      ingredient: RecipeMainIngredient | undefined
    ): void {
      if (ingredient) {
        recipeStore.editIngredientTransport(ingredient);
      }
    }

    function closePortSelectionModal(): void {
      editingIngredientStore.doneEditingIngredient();
      tripOverviewModalsStore.closePortSelectionModal();
    }

    function getAddFlightsModalRoutes(): AddFlightsModalInitialRoute[] {
      const routes: AddFlightsModalInitialRoute[] = [];
      let previousStay: StayIngredient | undefined;

      recipeStore.stayOnlyRecipe.forEach((currentStay, index) => {
        const previousRouteData = getPreviousRouteData(
          currentStay,
          previousStay
        );
        const nextRouteData = getNextRouteData(currentStay);
        const previousIngredient = recipeStore.getPreviousIngredient(
          currentStay.ingredientId
        );

        const hasNoneSimpleTransport =
          previousIngredient &&
          isOwnTransportIngredient(previousIngredient) &&
          previousIngredient.ownTransportType === TransportType.None;

        const hasFerryConnection =
          previousIngredient && isFerryIngredient(previousIngredient);

        const hasNonFlightTransport =
          previousIngredient && isFlightIngredient(previousIngredient);

        routes.push({
          from: previousRouteData.from,
          to: previousRouteData.to,
          ingredientId: currentStay.ingredientId,
          isLast: false,
          numberOfNights: currentStay.numberOfNights,
          isEnabled:
            !hasFerryConnection &&
            !hasNonFlightTransport &&
            !!hasNoneSimpleTransport,
        });

        if (index === recipeStore.stayOnlyRecipe.length - 1) {
          const nextIngredient = recipeStore.getNextIngredient(
            currentStay.ingredientId
          );

          const hasFerryConnection =
            nextIngredient && isFerryIngredient(nextIngredient);

          const lastRecipeIngredient = recipeStore.recipe.at(-1);

          let hasNonFlightEndTransport =
            lastRecipeIngredient &&
            isOwnTransportIngredient(lastRecipeIngredient) &&
            lastRecipeIngredient.ownTransportType !== TransportType.None;

          if (nextIngredient) {
            hasNonFlightEndTransport =
              !isFlightIngredient(nextIngredient) && hasNonFlightEndTransport;
          }

          routes.push({
            from: nextRouteData.from,
            to: nextRouteData.to,
            ingredientId: currentStay.ingredientId,
            isLast: true,
            numberOfNights: currentStay.numberOfNights,
            isEnabled: !hasFerryConnection && !hasNonFlightEndTransport,
          });
        }

        previousStay = currentStay;
      });

      return routes;
    }

    function getPreviousRouteData(
      currentStay: StayIngredient,
      previousStay?: StayIngredient
    ): RouteData {
      const previousIngredient = recipeStore.getPreviousIngredient(
        currentStay.ingredientId
      );
      const hasPreviousFlight =
        previousIngredient && isFlightIngredient(previousIngredient);

      return {
        from: hasPreviousFlight
          ? getRouteDataFromTransportIngredient(previousIngredient, 'from')
          : previousStay
          ? getStayRouteData(previousStay)
          : undefined,
        to: hasPreviousFlight
          ? getRouteDataFromTransportIngredient(previousIngredient, 'to')
          : getStayRouteData(currentStay),
      };
    }

    function getNextRouteData(currentStay: StayIngredient): RouteData {
      const nextIngredient = recipeStore.getNextIngredient(
        currentStay.ingredientId
      );
      const hasNextFlight =
        nextIngredient && isFlightIngredient(nextIngredient);

      return {
        from: hasNextFlight
          ? getRouteDataFromTransportIngredient(nextIngredient, 'from')
          : getStayRouteData(currentStay),
        to: hasNextFlight
          ? getRouteDataFromTransportIngredient(nextIngredient, 'to')
          : undefined,
      };
    }

    function getRouteDataFromTransportIngredient(
      ingredient: FlightIngredient,
      direction: 'from' | 'to'
    ): AddFlightsModalRouteStopData {
      return {
        displayText:
          direction === 'from'
            ? ingredient.fromPortDisplayText ?? ''
            : ingredient.toPortDisplayText ?? '',
        searchText:
          direction === 'from'
            ? ingredient.fromPortText ?? ''
            : ingredient.toPortText ?? '',
      };
    }

    function getStayRouteData(
      stay: StayIngredient
    ): AddFlightsModalRouteStopData {
      return {
        displayText: stay.displayText,
        searchText: stay.getSearchText(),
      };
    }

    function confirmAddFlightsModal(routes: AddFligthsModalRoute[]): void {
      tripOverviewModalsStore.closeAddFlightsModal();
      routes.forEach((route) => {
        const ingredient = recipeStore.getStayIngredient(route.ingredientId);

        const stayIndex = recipeStore.getIngredientIndex(
          ingredient.ingredientId
        );

        const previousIngredient = route.isLast
          ? recipeStore.recipe[recipeStore.recipe.length - 1]
          : recipeStore.recipe[stayIndex - 1];

        const isPreviousIngredientFlight = previousIngredient
          ? isFlightIngredient(previousIngredient)
          : false;

        const isPreviousFerryIngredient = previousIngredient
          ? isFerryIngredient(previousIngredient)
          : false;

        const isPreviousOwnTransportIngredient = previousIngredient
          ? isOwnTransportIngredient(previousIngredient)
          : false;

        if (!route.isEnabled) {
          if (isPreviousIngredientFlight) {
            if (route.isLast) {
              recipeStore.recipe.splice(recipeStore.recipe.length - 1, 1);
            } else {
              recipeStore.recipe.splice(stayIndex - 1, 1);
            }
          }
          return;
        }
        if (route.isLast) {
          const endLocationIngredient = recipeStore.endLocationIngredient;

          setEditingIngredient(endLocationIngredient);
        } else {
          setEditingIngredient(ingredient);
        }

        recipeStore.createTransportIngredient(ConnectionType.FLIGHT);
        const newIngredientId =
          recipeStore.settingTransportIngredient?.ingredientId;
        if (route.fromAirport.code && route.fromAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setDepartureAirport({
            iata: route.fromAirport.code,
            name: route.fromAirport.name,
            displayingText: route.fromAirport.label,
          });
        }

        if (route.toAirport.code && route.toAirport.label) {
          editingIngredientStore.editingFlightIngredient?.setArrivalAirport({
            iata: route.toAirport.code,
            name: route.toAirport.name,
            displayingText: route.toAirport.label,
          });
        }

        if (editingIngredientStore.editingTransportIngredient) {
          if (newIngredientId) {
            const ingredientIndex =
              recipeStore.getIngredientIndex(newIngredientId);
            if (
              isPreviousIngredientFlight ||
              isPreviousFerryIngredient ||
              isPreviousOwnTransportIngredient
            ) {
              if (route.isLast) {
                const ingredientToReplace =
                  recipeStore.recipe[recipeStore.recipe.length - 1];
                if (ingredientToReplace) {
                  recipeStore.replaceIngredient(
                    ingredientToReplace,
                    editingIngredientStore.editingTransportIngredient
                  );
                }
              } else {
                const ingredientToReplace = recipeStore.recipe[stayIndex - 1];
                if (ingredientToReplace) {
                  recipeStore.replaceIngredient(
                    ingredientToReplace,
                    editingIngredientStore.editingTransportIngredient
                  );
                }
              }
            } else {
              recipeStore.addIngredient(
                editingIngredientStore.editingTransportIngredient,
                ingredientIndex
              );
            }
            if (editingIngredientStore.editingFlightIngredient) {
              editingIngredientStore.editingFlightIngredient.filters.maxNumberOfStops =
                route.isDirect ? 0 : undefined;
            }
            editingIngredientStore.doneEditingIngredient();
          }
        }
      });
    }

    function showFerryDetailsModal(ingredientId: IngredientId): void {
      const ferryResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFerryIngredientResponse;
      const ferryStaticInfo = tripStore.getTransportStaticInfo(ferryResponse);

      const hasUserSelectedFerryTicketCategory =
        upsellStore.userSelectedFerryTicketCategory?.[
          ferryResponse.addToBasketToken
        ] !== undefined;
      const ticketType = hasUserSelectedFerryTicketCategory
        ? upsellStore.userSelectedFerryTicketCategory?.[
            ferryResponse.addToBasketToken
          ]
        : upsellStore.ferryTicketCategoryDictionary?.[
            ferryResponse.addToBasketToken
          ]?.[0];

      const ferryDetails = getFerryDetails(
        ferryResponse,
        ferryStaticInfo,
        ticketType?.type,
        tripStore.ferryVehicleTypes
      );
      if (ferryDetails) {
        tripOverviewModalsStore.openFerryDetailsModal(ferryDetails);
      } else {
        throw new Error("Can't open ferry details!");
      }
    }

    function showFlightDetailsModal(ingredientId: IngredientId): void {
      const flightResponse = itineraryStore.getTransportIngredientResponse(
        ingredientId
      ) as AvailableFlightIngredientResponse;

      const flightStaticInfo = tripStore.getTransportStaticInfo(flightResponse);

      const flightDetails = getFlightDetails(flightResponse, flightStaticInfo);
      if (flightDetails) {
        tripOverviewModalsStore.openFlightDetailsModal(flightDetails);
      } else {
        throw new Error("Can't open flight details!");
      }
    }

    function loadUserGeolocationToMapState(): void {
      if (geolocationStore.userGeolocation) {
        return;
      }

      geolocationStore.loadUserCoordinates().then(() => {
        const coordinates = geolocationStore.userGeolocation;

        if (coordinates) {
          if (!mapStore.startingLocation) {
            mapStore.setPointLocationFromPosition(
              coordinates,
              MyPositionType.Start
            );
          }

          if (!mapStore.endLocation) {
            mapStore.setPointLocationFromPosition(
              coordinates,
              MyPositionType.End
            );
          }
        }
      });
    }

    function scrollToCalendar(): void {
      const thresholdToPulse = 1000;
      const calendarElement = document.getElementById(
        TripOverviewTemplateIds.PriceCalendar
      );

      assertValue(calendarElement);

      calendarElement.scrollIntoView({
        behavior: 'smooth',
      });

      setTimeout(() => {
        priceCalendarStore.showPulsingIndicator();
      }, thresholdToPulse);
    }

    function getUnavailabilityMessage(
      segmentType: ItinerartBookableSegmentType,
      reasons: UnavailabilityReason[]
    ): string {
      switch (segmentType) {
        case ItinerarySegmentType.Accommodation:
          return getUnavailableAccommodationMessage(reasons);

        case ItinerarySegmentType.Activity:
          return getUnavailableActivityMessage(reasons);

        case ItinerarySegmentType.Flight:
          return getUnavailableFlightMessage(reasons);

        case ItinerarySegmentType.Ferry:
          return getUnavailableFerryMessage(reasons);

        default:
          exhaustiveGuard(
            segmentType,
            `No unavailability message for this segment type: ${segmentType}`
          );
      }
    }

    function changeLocation(
      segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment
    ): void {
      if (segment.segmentType === ItinerarySegmentType.MiddleLocation) {
        tripOverviewModalsStore.openEditTripModal({
          stepType: EditTripStepType.UpdateDestination,
          currentEditingSegment: {
            destinationName: getSearchTextFromLocationSegment(segment.location),
            ingredientId: segment.ingredientId,
            numberOfNights: segment.numberOfNights,
          },
        });
      } else {
        const searchText = getSearchTextFromLocationSegment(segment.location);
        const modalType = getAutocompleteTypeFromLocationSegment(segment);
        tripOverviewModalsStore.openDestinationSelectorModal(
          searchText,
          undefined,
          modalType
        );
      }
    }

    function confirmAlternativeRoomSelection(
      segment: AlternativeRoomSuggestionAccommodationSegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeStaySelected();

      const heartedHotel: HeartedHotel = {
        code: segment.alternativeSuggestionData.hotelCode,
        isRefundable: segment.alternativeSuggestionData.isRefundable,
        rooms: segment.alternativeSuggestionData.rooms.map((room) => ({
          code: room.roomCode,
          boardBasis: room.mealCode,
        })),
      };

      tripStore.toggleHeartHotel(heartedHotel, segment.context.ingredientId);
      mapStore.updateMapShouldCenterOnMarkers(true);
      editingIngredientStore.doneEditingIngredient();
    }

    function confirmAlternativeTicketSelection(
      segment: AlternativeTicketSuggestionActivitySegment
    ): void {
      statsigEventsStore.logTripOverviewAlternativeActivitySelected();

      const activityIngredient = recipeStore.getActivityIngredient(
        segment.context.ingredientId
      );

      activityIngredient.tickets = segment.alternativeSuggestionData.tickets;
    }

    const stopWatch = watch(isLoading, () => {
      const hasLandedInTripOverview =
        routerStore.landingRoute === TripPlannerPage.TripOverview;

      if (!hasLandedInTripOverview) {
        stopWatch();
        return;
      }

      if (!isLoading.value) {
        const isInfiniteLoad = !anyItineraryLoaded.value;

        if (isInfiniteLoad) {
          statsigEventsStore.logTripOverviewInfiniteLoad();
        }

        stopWatch();
      }
    });

    function addAccommodationToOwnStayIngredient(
      ownStayIngredientId: IngredientId
    ): void {
      const ownStayIngredient =
        recipeStore.getOwnStayIngredient(ownStayIngredientId);
      setDestinationTextToOwnStayIngredient(
        ownStayIngredient.ingredientId,
        ownStayIngredient.searchTerm,
        ownStayIngredient.displayText
      );
    }

    function setDestinationTextToOwnStayIngredient(
      ownStayIngredientId: IngredientId,
      newSearchTerm: SearchTerm,
      newDisplayText?: string
    ): void {
      const ownStayIngredient =
        recipeStore.getOwnStayIngredient(ownStayIngredientId);

      const hotelIngredient = new HotelIngredient(newSearchTerm);
      hotelIngredient.numberOfNights = ownStayIngredient.numberOfNights;
      hotelIngredient.subIngredients = ownStayIngredient.subIngredients.map(
        (activitySubIngredient) => {
          const newActivitySubIngredient = new ActivityIngredient(
            activitySubIngredient.code,
            hotelIngredient.ingredientId
          );

          newActivitySubIngredient.offset = activitySubIngredient.offset;
          newActivitySubIngredient.tickets = activitySubIngredient.tickets;

          return newActivitySubIngredient;
        }
      );

      if (newDisplayText) {
        hotelIngredient.displayText = newDisplayText;
      }

      recipeStore.replaceIngredient(ownStayIngredient, hotelIngredient);
    }

    function updateSuggestionType(
      ingredientId: IngredientId,
      suggestionType: SuggestionType
    ): void {
      const ingredient = recipeStore.getIngredient(ingredientId);

      if (
        isOwnStayIngredient(ingredient) ||
        isActivityIngredient(ingredient) ||
        isOwnTransportIngredient(ingredient)
      ) {
        return;
      }

      const availabilityOrder =
        suggestionType === SuggestionType.MostPopular
          ? AvailabilityOrder.Popular
          : AvailabilityOrder.Cheapest;
      ingredient.availabilityOrder = availabilityOrder;
    }

    function getFirstDestinationCityName(): string | undefined {
      const firstDestination = tripItinerary.value.find(
        (segment): segment is ItineraryMiddleLocationSegment =>
          isItineraryMiddleLocationSegment(segment)
      );

      return firstDestination?.location.name;
    }

    return {
      // State
      isFirstTripItineraryRender,

      // Actions
      setUserSelectedTransport,
      initializeView,
      deleteIngredientById,
      selectTransport,
      closePortSelectionModal,
      getAddFlightsModalRoutes,
      showFerryDetailsModal,
      showFlightDetailsModal,
      confirmAddFlightsModal,
      scrollToCalendar,
      getUnavailabilityMessage,
      changeLocation,
      confirmAlternativeRoomSelection,
      confirmAlternativeTicketSelection,
      confirmAlternativeFlight,
      addAccommodationToOwnStayIngredient,
      setDestinationTextToOwnStayIngredient,
      updateSuggestionType,
      getFirstDestinationCityName,

      // Getters
      hasUserSelectedTransport: readonlyRef(hasUserSelectedTransport),
      isLoading,
      isLoadingSimpleTransportSegmentDetails,
      anyItineraryLoaded,
      hasRecipeIngredients,
      tripItinerary,
      tripItineraryLoader,
      homeFlightData,
      numberOfDestinations,
      numberOfNights,
    };
  }
);
